import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, ArrowLeft, KeyRound, Copy, Check } from 'lucide-react';
import { usersService } from '../../services/usersService';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { UserRole } from '../../types';

const roleLabels: Record<UserRole, string> = {
  admin: 'Administrátor',
  supplier: 'Dodavatel',
  pre_approver: 'Předschvalovatel',
  approver: 'Schvalovatel',
  end_user: 'Prodejce',
};

// Stejna pravidla jako pri hromadnem zakladani dodavatelu (21. 10. 2026): 12 znaku
// bez zamenitelnych (0/O, 1/l/I), aby slo heslo bezpecne nadiktovat nebo prepsat.
const ZNAKY_HESLA = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const vygenerujHeslo = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(12)), (b) => ZNAKY_HESLA[b % ZNAKY_HESLA.length]).join('');

export const UserFormPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'end_user' as UserRole,
    isActive: true,
  });
  // Vygenerovane heslo se ukaze citelne, aby ho admin mohl predat; rucne psane zustava skryte.
  const [hesloViditelne, setHesloViditelne] = useState(false);
  const [zkopirovano, setZkopirovano] = useState(false);

  const generujHeslo = () => {
    setFormData({ ...formData, password: vygenerujHeslo() });
    setHesloViditelne(true);
    setZkopirovano(false);
  };

  const kopirujHeslo = async () => {
    try {
      await navigator.clipboard.writeText(formData.password);
      setZkopirovano(true);
      setTimeout(() => setZkopirovano(false), 2000);
    } catch {
      alert('Zkopírování se nezdařilo – heslo lze označit a zkopírovat ručně.');
    }
  };

  const { data: user } = useQuery({
    queryKey: ['users', id],
    queryFn: () => usersService.getUser(id!),
    enabled: isEdit,
  });

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        password: '', // Heslo se při editaci nevyplňuje
        role: user.role,
        isActive: user.isActive ?? true,
      });
    }
  }, [user]);

  const saveMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      if (isEdit && id) {
        // Heslo se posílá jen když ho admin vyplnil – prázdné pole znamená "neměnit".
        const { password, ...updateData } = data;
        return usersService.updateUser(
          id,
          password ? { ...updateData, password } : updateData,
        );
      } else {
        return usersService.createUser(data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      navigate('/admin/users');
    },
    // Bez tohohle selhavalo ulozeni potichu - chyba ze serveru se nikde neukazala.
    // Validacni chyby chodi jako pole hlasek, proto to spojeni.
    onError: (e: any) => {
      const zprava = e?.response?.data?.message;
      alert(Array.isArray(zprava) ? zprava.join('\n') : zprava || 'Uživatele se nepodařilo uložit.');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveMutation.mutate(formData);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <Button variant="outline" onClick={() => navigate('/admin/users')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zpět na uživatele
        </Button>
        <h1 className="text-3xl font-bold text-gray-900">
          {isEdit ? 'Upravit uživatele' : 'Nový uživatel'}
        </h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-lg shadow p-6 space-y-6">
          <Input
            label="Jméno *"
            maxLength={100}
            value={formData.firstName}
            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            required
          />

          <Input
            label="Příjmení *"
            maxLength={100}
            value={formData.lastName}
            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            required
          />

          <Input
            label="Email *"
            type="email"
            maxLength={254}
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />

          <div>
            <Input
              label={isEdit ? 'Nové heslo (volitelné)' : 'Heslo *'}
              type={hesloViditelne ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => {
                setFormData({ ...formData, password: e.target.value });
                setZkopirovano(false);
              }}
              required={!isEdit}
              minLength={6}
              placeholder={isEdit ? 'Vyplňte pouze pro změnu hesla (min. 6 znaků)' : 'Minimálně 6 znaků'}
              autoComplete="new-password"
            />
            <div className="mt-2 flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={generujHeslo}>
                <KeyRound className="w-4 h-4 mr-2" />
                Vygenerovat heslo
              </Button>
              {hesloViditelne && formData.password && (
                <Button type="button" variant="outline" size="sm" onClick={kopirujHeslo}>
                  {zkopirovano ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  {zkopirovano ? 'Zkopírováno' : 'Kopírovat heslo'}
                </Button>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              {Object.entries(roleLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              <span className="font-medium">Aktivní účet</span>
            </label>
            <p className="mt-1 text-sm text-gray-500">
              Neaktivní uživatel se nepřihlásí a nedostává žádné e-mailové notifikace.
              Vlastní účet deaktivovat nelze.
            </p>
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-4">
          <Button type="button" variant="outline" onClick={() => navigate('/admin/users')}>
            Zrušit
          </Button>
          <Button type="submit" isLoading={saveMutation.isPending}>
            <Save className="w-4 h-4 mr-2" />
            {isEdit ? 'Aktualizovat uživatele' : 'Vytvořit uživatele'}
          </Button>
        </div>
      </form>
    </div>
  );
};
